import {
  createConfiguredFieldInitialValue,
  type ConfiguredFieldInitialValueContext,
  type ConfiguredFieldOptions,
  type ConfiguredFieldValuesInput,
  type ConfiguredFieldValuesSchemaContext,
} from '@cims-online/shared'
import type {
  ConfiguredFieldsBaseValues,
  ConfiguredFieldsContext,
  ConfiguredFieldValueContext,
  ConfiguredFieldsFormDefaults,
  ConfiguredFieldsPayItem,
  ConfiguredFieldsSession,
} from './types'

/** Captures field defaults and validation context together for one form opening. */
export function createConfiguredFieldsSession(
  context: ConfiguredFieldsContext
): ConfiguredFieldsSession {
  const { fields, baseValues, initialValueContext } = context
  return {
    context,
    fieldValueContexts: createConfiguredFieldValueContexts(
      fields,
      baseValues,
      initialValueContext
    ),
    initialValues: createConfiguredFieldsInitialValues(
      fields,
      baseValues,
      initialValueContext
    ),
  }
}

/** Initializes every configured field, including read-only and hidden values. */
function createConfiguredFieldsInitialValues(
  fields: readonly ConfiguredFieldOptions[],
  baseValues: ConfiguredFieldsBaseValues = {},
  initialValueContext: ConfiguredFieldInitialValueContext = {}
): ConfiguredFieldValuesInput {
  const values: ConfiguredFieldValuesInput = {}
  for (const field of fields)
    values[field.id] = createConfiguredFieldInitialValue(
      field,
      baseValues[field.id],
      initialValueContext
    )
  return values
}

function optionIdsFromValue(
  field: ConfiguredFieldOptions,
  value: unknown
): string[] {
  if (field.type === 'selected_by') {
    if (
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      'optionId' in value &&
      typeof value.optionId === 'string'
    )
      return [value.optionId]
    return []
  }

  if (!Array.isArray(value)) return []
  if (field.type === 'pay_items' || field.type === 'select')
    return value.filter(
      (optionId): optionId is string => typeof optionId === 'string'
    )
  if (field.type === 'approvals')
    return value.flatMap((option) =>
      typeof option === 'object' &&
      option !== null &&
      'optionId' in option &&
      typeof option.optionId === 'string'
        ? [option.optionId]
        : []
    )
  return []
}

function payItemValueContext(
  field: Extract<ConfiguredFieldOptions, { type: 'pay_items' }>,
  baseValue: unknown,
  { activePayItemIds, payItems = [] }: ConfiguredFieldValueContext
) {
  const currentOptionIds = optionIdsFromValue(field, baseValue)
  const payItemsById = new Map(
    payItems.map((payItem) => [payItem.globalId.toLowerCase(), payItem])
  )
  const activeOptionIds = [
    ...(activePayItemIds ??
      payItems
        .filter(({ deleted }) => !deleted)
        .map(({ globalId }) => globalId)),
  ]

  for (const optionId of currentOptionIds) {
    const payItem = payItemsById.get(optionId.toLowerCase())
    if (payItem && !payItem.deleted && !activeOptionIds.includes(optionId))
      activeOptionIds.push(optionId)
  }

  return {
    activeOptionIds,
    retainedOptionIds: field.forbidDeleted
      ? []
      : currentOptionIds.filter(
          (optionId) => payItemsById.get(optionId.toLowerCase())?.deleted
        ),
  }
}

/**
 * Builds field validation contexts from the overlay's persisted-value snapshot.
 *
 * Select-like fields retain only deleted choices already on the record. Pay
 * items additionally reflect the loaded project choices so a forbidden deleted
 * item can be identified before submission.
 */
function createConfiguredFieldValueContexts(
  fields: readonly ConfiguredFieldOptions[],
  baseValues: ConfiguredFieldsBaseValues = {},
  initialValueContext: ConfiguredFieldValueContext = {}
): ConfiguredFieldValuesSchemaContext {
  const contexts: ConfiguredFieldValuesSchemaContext = {}

  for (const field of fields) {
    if (field.type === 'pay_items') {
      contexts[field.id] = payItemValueContext(
        field,
        baseValues[field.id],
        initialValueContext
      )
      continue
    }

    if (
      field.type !== 'approvals' &&
      field.type !== 'select' &&
      field.type !== 'selected_by'
    )
      continue

    const deletedOptionIds = new Set(
      field.options.filter(({ deleted }) => deleted).map(({ id }) => id)
    )
    contexts[field.id] = {
      retainedOptionIds: optionIdsFromValue(field, baseValues[field.id]).filter(
        (optionId) => deletedOptionIds.has(optionId)
      ),
    }
  }

  return contexts
}

/**
 * Combines the record's pay-item metadata with the project's loaded items.
 *
 * The project list wins for items it contains; the record's copies keep labels
 * and deleted flags for historical selections the project list omits.
 */
function mergePayItems(
  recordPayItems: readonly ConfiguredFieldsPayItem[] = [],
  loadedPayItems: readonly ConfiguredFieldsPayItem[]
): ConfiguredFieldsPayItem[] {
  const byId = new Map(
    recordPayItems.map((item) => [item.globalId.toLowerCase(), item])
  )
  for (const item of loadedPayItems) byId.set(item.globalId.toLowerCase(), item)
  return [...byId.values()]
}

/**
 * Resolves a field form's defaults against the project's pay items.
 *
 * Until project pay items load, the session's opening snapshot is used as is.
 * Once they load, initial values and validation contexts are rebuilt with the
 * active item list, so `'all'` pay-item defaults select every active item and
 * forbidden deleted selections can be identified.
 *
 * @param loadedPayItems The project's pay items, or `undefined` while loading.
 */
export function resolveConfiguredFieldsFormDefaults(
  session: ConfiguredFieldsSession,
  loadedPayItems: readonly ConfiguredFieldsPayItem[] | undefined
): ConfiguredFieldsFormDefaults {
  const { fields, baseValues, initialValueContext } = session.context
  const payItems = mergePayItems(
    initialValueContext?.payItems,
    loadedPayItems ?? []
  )
  const deletedPayItemIds = payItems
    .filter(({ deleted }) => deleted)
    .map(({ globalId }) => globalId)
  if (!loadedPayItems)
    return {
      deletedPayItemIds,
      fieldValueContexts: session.fieldValueContexts,
      initialValues: session.initialValues,
      payItems,
    }

  const context: ConfiguredFieldValueContext = {
    activePayItemIds: loadedPayItems
      .filter(({ deleted }) => !deleted)
      .map(({ globalId }) => globalId),
    payItems,
  }
  return {
    deletedPayItemIds,
    fieldValueContexts: createConfiguredFieldValueContexts(
      fields,
      baseValues,
      context
    ),
    initialValues: createConfiguredFieldsInitialValues(
      fields,
      baseValues,
      context
    ),
    payItems,
  }
}
